// ═══════════════════════════════════════════════════════════
// BOOST MANAGER - Speed Power-ups
// ═══════════════════════════════════════════════════════════

import { Vector2, Bounds, Boost } from './types.js';
import { Utils } from './Utils.js';

export class BoostManager {
    private boosts: Boost[] = [];
    private bounds: Bounds;
    private boostSize: number = 25;
    private spawnTimer: number = 0;
    private spawnInterval: number = 15; // Spawn a boost every 15 seconds

    constructor(bounds: Bounds) {
        this.bounds = bounds;
    }

    update(deltaTime: number): void {
        // Update existing boosts (animation)
        for (const boost of this.boosts) {
            boost.pulsePhase += deltaTime * 4;
            boost.glowIntensity = 0.5 + Math.sin(boost.pulsePhase) * 0.5;
        }

        // Spawn new boosts periodically
        this.spawnTimer += deltaTime;
        if (this.spawnTimer >= this.spawnInterval && this.boosts.length < 2) {
            this.spawnBoost();
            this.spawnTimer = 0;
        }
    }

    private spawnBoost(): void {
        // Padding from edges to avoid spawning too close to walls
        const padding = 50;

        // Random position anywhere on screen (with padding)
        const position: Vector2 = {
            x: Utils.random(padding, this.bounds.width - padding),
            y: Utils.random(padding, this.bounds.height - padding)
        };

        this.boosts.push({
            position,
            size: this.boostSize,
            pulsePhase: Utils.random(0, Math.PI * 2),
            glowIntensity: 1
        });
    }

    checkCollision(ballPosition: Vector2, ballSize: number): boolean {
        const ballRadius = ballSize / 2;

        for (let i = this.boosts.length - 1; i >= 0; i--) {
            const boost = this.boosts[i];
            const distance = Math.sqrt(
                Math.pow(ballPosition.x - boost.position.x, 2) +
                Math.pow(ballPosition.y - boost.position.y, 2)
            );

            // Check if ball collects the boost
            if (distance < ballRadius + boost.size / 2) {
                // Remove the boost
                this.boosts.splice(i, 1);
                return true;
            }
        }

        return false;
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const boost of this.boosts) {
            this.renderBoost(ctx, boost);
        }
    }

    private renderBoost(ctx: CanvasRenderingContext2D, boost: Boost): void {
        const offsetY = this.bounds.offsetY || 0;
        ctx.save();
        ctx.translate(boost.position.x, boost.position.y + offsetY);

        // Pulsing scale effect
        const pulseScale = 1 + Math.sin(boost.pulsePhase) * 0.15;
        ctx.scale(pulseScale, pulseScale);

        const size = boost.size;

        // Draw outer glow (animated)
        const glowGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 2);
        glowGradient.addColorStop(0, `rgba(255, 215, 0, ${boost.glowIntensity * 0.6})`);
        glowGradient.addColorStop(0.5, `rgba(255, 165, 0, ${boost.glowIntensity * 0.3})`);
        glowGradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = glowGradient;
        ctx.fillRect(-size * 2, -size * 2, size * 4, size * 4);

        // Draw main star body
        this.drawStar(ctx, 0, 0, 5, size, size / 2);

        // Draw inner highlight
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        this.drawStar(ctx, 0, 0, 5, size * 0.5, size * 0.25);

        // Draw lightning bolt icon
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#ffa500';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-4, -8);
        ctx.lineTo(2, -2);
        ctx.lineTo(-2, -2);
        ctx.lineTo(4, 8);
        ctx.lineTo(-2, 2);
        ctx.lineTo(2, 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        ctx.restore();
    }

    private drawStar(ctx: CanvasRenderingContext2D, cx: number, cy: number, spikes: number, outerRadius: number, innerRadius: number): void {
        let rot = Math.PI / 2 * 3;
        let x = cx;
        let y = cy;
        const step = Math.PI / spikes;

        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);

        for (let i = 0; i < spikes; i++) {
            x = cx + Math.cos(rot) * outerRadius;
            y = cy + Math.sin(rot) * outerRadius;
            ctx.lineTo(x, y);
            rot += step;

            x = cx + Math.cos(rot) * innerRadius;
            y = cy + Math.sin(rot) * innerRadius;
            ctx.lineTo(x, y);
            rot += step;
        }

        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();

        // Gradient fill
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, outerRadius);
        gradient.addColorStop(0, '#ffff00');
        gradient.addColorStop(0.5, '#ffd700');
        gradient.addColorStop(1, '#ffa500');
        ctx.fillStyle = gradient;
        ctx.fill();
    }

    reset(): void {
        this.boosts = [];
        this.spawnTimer = 0;
    }

    // Manually spawn a boost (for testing or initial setup)
    forceSpawn(): void {
        this.spawnBoost();
    }
}
